import { showDialog } from '@adornis/dialog/dialog-functions.js';
import { CheckboxInput } from '@adornis/formfield/fields/checkbox-input.js';
import { DateInput } from '@adornis/formfield/fields/date-input.js';
import { DropdownSelectionInput } from '@adornis/formfield/fields/dropdown-selection-input.js';
import { TextInput } from '@adornis/formfield/fields/text-input.js';
import { useForm } from '@adornis/forms/use-form.js';
import { stateful, useState } from '@adornis/functional-lit/stateful.js';
import { html } from 'lit';
import { getScopedFieldEngineers } from '../../api/scoped-queries.js';
import { SmartTeamsAvailability, SmartTeamsFieldEngineer } from '../../db/index.js';

const CreateAvailabilityForm = stateful(
  (props: {
    fieldEngineers: SmartTeamsFieldEngineer[];
    onSubmit: (data: SmartTeamsAvailability) => void;
    onCancel: () => void;
  }) => {
    const [initialDocument] = useState(
      new SmartTeamsAvailability({
        startTime: '08:00',
        endTime: '17:00',
        useDefaultLocation: true,
      }),
    );

    const { document, field, validate } = useForm<SmartTeamsAvailability>(initialDocument, {
      validator: SmartTeamsAvailability.prototype.validator.functions,
    });

    return html`
      <div class="flex flex-col gap-4">
        <h3 class="text-lg font-bold">Create new Availability</h3>

        <label class="form-control w-full">
          <div class="label"><span class="label-text">Field Engineer *</span></div>
          ${DropdownSelectionInput({
            value: props.fieldEngineers.find(fe => fe._id === field('fieldEngineerId').value) ?? null,
            selectables: props.fieldEngineers,
            renderString: (fe: SmartTeamsFieldEngineer) => `${fe.name ?? fe._id}${fe.email ? ` (${fe.email})` : ''}`,
            resolveID: (fe: SmartTeamsFieldEngineer) => fe._id ?? '',
            placeholder: 'Select Field Engineer...',
            error: field('fieldEngineerId').error,
            onPicked: value => {
              const fe = value as SmartTeamsFieldEngineer | null;
              field('fieldEngineerId').onPicked?.(fe?._id ?? null);
            },
          })}
        </label>

        <label class="form-control w-full">
          <div class="label"><span class="label-text">Date *</span></div>
          ${DateInput({ ...field('date'), type: 'date' })}
        </label>

        <div class="grid grid-cols-2 gap-4">
          <label class="form-control w-full">
            <div class="label"><span class="label-text">Start time *</span></div>
            ${TextInput({ ...field('startTime'), placeholder: '08:00' })}
          </label>
          <label class="form-control w-full">
            <div class="label"><span class="label-text">End time *</span></div>
            ${TextInput({ ...field('endTime'), placeholder: '17:00' })}
          </label>
        </div>

        ${CheckboxInput({
          ...field('useDefaultLocation'),
          label: 'Use default location of Field Engineer',
        })}

        <div class="flex justify-end gap-2 mt-2">
          <button class="btn btn-ghost" @click=${props.onCancel}>Cancel</button>
          <button
            class="btn btn-primary"
            @click=${async () => {
              const error = await validate();
              console.log('document: ', document);
              if (error) return;
              props.onSubmit(document);
            }}
          >
            Create
          </button>
        </div>
      </div>
    `;
  },
);

/**
 * Shows a dialog to create a new availability.
 * Returns the new availability's ID if created, or null if cancelled.
 */
export async function showCreateAvailabilityDialog(): Promise<string | null> {
  const fieldEngineers = (await getScopedFieldEngineers()({ _id: 1, name: 1, email: 1 })) as SmartTeamsFieldEngineer[];

  if (!fieldEngineers || fieldEngineers.length === 0) {
    await showDialog<void>({
      content: ok => html`
        <div class="flex flex-col gap-4">
          <h3 class="text-lg font-bold text-error">No Field Engineers available</h3>
          <p>Please create a Field Engineer first before creating an availability.</p>
          <div class="flex justify-end">
            <button class="btn btn-primary" @click=${() => ok()}>OK</button>
          </div>
        </div>
      `,
    }).catch(() => {});
    return null;
  }

  const availability = await showDialog<SmartTeamsAvailability | null>({
    modalBoxClasses: 'max-w-lg',
    content: (ok, cancel) =>
      CreateAvailabilityForm({
        fieldEngineers,
        onSubmit: ok,
        onCancel: () => cancel(),
      }),
  }).catch(() => null);

  if (!availability) return null;

  const newId = await availability.create?.();
  return newId ?? null;
}
